// Makes the face in a PHOTO (or any front-facing picture: a portrait, an illustration, a mascot) speak. The visitor marks the two eyes and the two
// corners of the mouth; from those four points the lower face is stretched open like a jaw dropping, the inside of the mouth (teeth and tongue)
// is drawn in the opening, the eyelids close in a blink, and the head moves a little. The mouth follows the real sound (lipsync.js).
//
// Honest limits: it works best on a face that looks straight at the camera, and it is a lip-sync *puppet*, not a photo-real AI video.

const clamp = (x, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, x));

// where the eyes and the mouth usually are in a head-and-shoulders portrait: the visitor moves them onto the real face
export const DEFAULT_MARKS = { le: [0.4, 0.34], re: [0.6, 0.34], ml: [0.44, 0.52], mr: [0.56, 0.52] };

// The face measured in the picture's own pixels. marks: { le, re, ml, mr } as shares (0-1) of the width and height.
export function faceGeometry(marks, iw, ih) {
  const p = (key) => ({ x: marks[key][0] * iw, y: marks[key][1] * ih });
  const le = p("le");
  const re = p("re");
  const ml = p("ml");
  const mr = p("mr");
  const ipd = Math.max(8, Math.hypot(re.x - le.x, re.y - le.y));              // the distance between the eyes is the ruler of the face
  const mouth = { x: (ml.x + mr.x) / 2, y: (ml.y + mr.y) / 2, halfWidth: Math.max(4, Math.hypot(mr.x - ml.x, mr.y - ml.y) / 2) };
  return { iw, ih, le, re, mouth, ipd, chinY: mouth.y + 0.75 * ipd };
}

// Whether four marks can be a face: left eye left of the right eye, the mouth below the eyes and not wider than the face.
export function marksAreValid(marks) {
  if (!marks) return false;
  const keys = ["le", "re", "ml", "mr"];
  if (!keys.every((k) => Array.isArray(marks[k]) && marks[k].length === 2 && marks[k].every((v) => typeof v === "number" && v >= 0 && v <= 1))) return false;
  const eyeGap = marks.re[0] - marks.le[0];
  const mouthWidth = marks.mr[0] - marks.ml[0];
  const eyesY = (marks.le[1] + marks.re[1]) / 2;
  const mouthY = (marks.ml[1] + marks.mr[1]) / 2;
  return eyeGap >= 0.03 && mouthWidth >= 0.015 && mouthWidth <= eyeGap * 1.6 && mouthY > eyesY + 0.01;
}

// How far the jaw drops (in the picture's pixels) for a mouth opened by `open` (0..1): at most a quarter of the distance between the eyes.
export const jawDrop = (open, ipd) => clamp(open) * 0.26 * ipd;

// The share of the jaw drop a vertical strip of the lower face gets, t = its distance from the middle of the mouth as a share (0..1) of the
// width that is moved: all of it in the middle, fading smoothly to nothing at the sides.
export const weightAt = (t) => (t < 0.4 ? 1 : 0.5 * (1 + Math.cos(Math.PI * clamp((t - 0.4) / 0.6))));
export const stripWeights = (count) => Array.from({ length: count }, (_, i) => weightAt(Math.abs((i + 0.5) / count - 0.5) * 2));

// A skin colour taken from the cheeks, for the eyelids (null when the picture cannot be read, e.g. it came from another site).
export function sampleSkin(img, geometry) {
  try {
    const size = 7;
    const canvas = document.createElement("canvas");
    canvas.width = size * 2;
    canvas.height = size;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const spots = [geometry.le, geometry.re].map((e) => ({ x: e.x, y: e.y + 0.55 * geometry.ipd }));
    spots.forEach((s, i) => ctx.drawImage(img, clamp(s.x - size / 2, 0, geometry.iw - size), clamp(s.y - size / 2, 0, geometry.ih - size), size, size, i * size, 0, size, size));
    const data = ctx.getImageData(0, 0, size * 2, size).data;
    let r = 0;
    let g = 0;
    let b = 0;
    let n = 0;
    for (let i = 0; i < data.length; i += 4) if (data[i + 3] > 200) { r += data[i]; g += data[i + 1]; b += data[i + 2]; n += 1; }
    if (!n) return null;
    const lift = 1.03;
    return `rgb(${Math.min(255, Math.round((r / n) * lift))},${Math.min(255, Math.round((g / n) * lift))},${Math.min(255, Math.round((b / n) * lift))})`;
  } catch {
    return null;
  }
}

const STRIPS = 36;

// Draws the picture with the face speaking into `dest` ({ x, y, w, h }: where the whole picture goes).
//   mouth = { open, wide, round }, blink 0..1, pose = headPose(...), motion = how much of the pose is used (1 for a cut-out person, less for a photo with a background)
export function drawPuppet(ctx, img, geometry, state, dest, skin = "rgb(205,160,130)", motion = 1) {
  const { mouth: m, ipd, iw, ih } = geometry;
  const { mouth, blink, pose } = state;
  const k = dest.w / iw;
  ctx.save();
  // the head's own movement: a turn about the base of the neck, a nod, a breath
  const pivotX = dest.x + m.x * k;
  const pivotY = dest.y + (geometry.chinY + 0.4 * ipd) * k;
  ctx.translate(pivotX + pose.dx * ipd * k * 2.4 * motion, pivotY + pose.dy * ipd * k * 2.4 * motion);
  ctx.rotate(pose.rot * motion);
  ctx.scale(1 + (pose.breathe - 1) * motion, 1 + (pose.breathe - 1) * motion);
  ctx.translate(-pivotX, -pivotY);

  ctx.drawImage(img, dest.x, dest.y, dest.w, dest.h);

  const drop = jawDrop(mouth.open, ipd);
  if (drop * k > 0.6) {
    const cy = m.y;
    const yb = Math.min(ih, geometry.chinY + 0.9 * ipd);
    const span = m.halfWidth * 1.9 + ipd * 0.15;
    const x0 = Math.max(0, m.x - span);
    const x1 = Math.min(iw, m.x + span);
    const sw = (x1 - x0) / STRIPS;
    const X = (x) => dest.x + x * k;
    const Y = (y) => dest.y + y * k;
    // the lower face moves down; the opening between is filled with the inside of the mouth
    for (let i = 0; i < STRIPS; i += 1) {
      const xs = x0 + i * sw;
      const d = drop * weightAt(Math.abs(xs + sw / 2 - m.x) / span);
      if (d < 0.3) continue;
      ctx.drawImage(img, xs, cy, sw + 0.6, yb - cy, X(xs), Y(cy + d), (sw + 0.6) * k, (yb - cy - d) * k);
      const inside = Math.abs(xs + sw / 2 - m.x) < m.halfWidth * 0.98;
      if (!inside) ctx.drawImage(img, xs, Math.max(0, cy - 2), sw + 0.6, 2, X(xs), Y(cy), (sw + 0.6) * k, (d + 0.6) * k);   // skin beside the mouth is stretched
    }
    const rx = m.halfWidth * (0.93 + 0.08 * mouth.wide - 0.3 * mouth.round);
    const ry = drop / 2 + ipd * 0.012;
    const cyMid = cy + drop / 2;
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(X(m.x), Y(cyMid), rx * k, ry * k, 0, 0, Math.PI * 2);
    ctx.clip();
    const g = ctx.createLinearGradient(0, Y(cy), 0, Y(cy + drop));
    g.addColorStop(0, "#2a0a12");
    g.addColorStop(1, "#4d1a24");
    ctx.fillStyle = g;
    ctx.fillRect(X(m.x - rx), Y(cy - 1), 2 * rx * k, (drop + 2) * k);
    const teethHeight = Math.min(drop * 0.5, ipd * 0.1);
    const tg = ctx.createLinearGradient(0, Y(cy), 0, Y(cy + teethHeight));
    tg.addColorStop(0, "#fbf8f2");
    tg.addColorStop(1, "#e2dbcf");
    ctx.fillStyle = tg;
    ctx.fillRect(X(m.x - rx * 0.9), Y(cy - 1), 2 * rx * 0.9 * k, (teethHeight + 1) * k);
    ctx.fillStyle = "#c4546a";
    ctx.beginPath();
    ctx.ellipse(X(m.x), Y(cy + drop * 0.92), rx * 0.58 * k, Math.max(1, drop * 0.34) * k, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    ctx.strokeStyle = "rgba(60,10,20,0.45)";
    ctx.lineWidth = Math.max(1, ipd * 0.012 * k);
    ctx.beginPath();
    ctx.ellipse(X(m.x), Y(cyMid), rx * k, ry * k, 0, 0, Math.PI * 2);
    ctx.stroke();
  }

  if (blink > 0.04) {
    [geometry.le, geometry.re].forEach((eye) => {
      const rx = 0.3 * ipd;
      const ry = 0.14 * ipd;
      ctx.save();
      ctx.beginPath();
      ctx.ellipse(X0(dest, eye.x, k), Y0(dest, eye.y, k), rx * k, ry * k, 0, 0, Math.PI * 2);
      ctx.clip();
      ctx.fillStyle = skin;
      ctx.fillRect(X0(dest, eye.x - rx, k), Y0(dest, eye.y - ry, k), 2 * rx * k, 2 * ry * k * clamp(blink));
      ctx.restore();
      if (blink > 0.55) {
        ctx.strokeStyle = "rgba(25,15,12,0.55)";
        ctx.lineWidth = Math.max(1, ipd * 0.02 * k);
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(X0(dest, eye.x - rx * 0.9, k), Y0(dest, eye.y + ry * 0.1, k));
        ctx.quadraticCurveTo(X0(dest, eye.x, k), Y0(dest, eye.y + ry * 0.5, k), X0(dest, eye.x + rx * 0.9, k), Y0(dest, eye.y + ry * 0.1, k));
        ctx.stroke();
      }
    });
  }
  ctx.restore();
}

const X0 = (dest, x, k) => dest.x + x * k;
const Y0 = (dest, y, k) => dest.y + y * k;
